/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable no-console */
/* eslint-disable import/no-extraneous-dependencies */
import {useEffect, useState} from 'react';
import ReactPaginate from 'react-paginate';
// eslint-disable-next-line import/named
import {confirmAlert} from 'react-confirm-alert';
import {useNavigate} from 'react-router-dom';

import type {Policy} from 'types/general';
import ModalUploadFile from './components/ModalUploadFile';

import 'react-confirm-alert/src/react-confirm-alert.css';
import {useAppSelector} from 'store/hooks';
import {hasExactRole} from 'utils';

const LIMIT = Number(process.env.REACT_APP_PAGINATE_LIMIT || 10);

type TProps = {
	getAllPolicies: Policy[];
	deletePolicy: (item: Policy) => void;
	updatePolicyInactivated: (item: Policy) => void;
	updatePolicyActivated: (item: Policy) => void;
	updatePolicyTest: (item: Policy) => void;
	updatePolicyUnsetTest: (item: Policy) => void;
	totalPolicies: number;
	onPage: (param: number) => void;
	timeStr: string;
	refetch: () => void;
	handleGetTextContent: (arg: Policy, redirect: boolean, edit: boolean) => void;
	handleGetTextContentToView: (arg: Policy) => void;
	onDownloadXML: (arg: Policy) => void;
};
type TData = {
	selected: number;
};

const RepositoryPolicy = ({
	getAllPolicies = [],
	totalPolicies,
	deletePolicy,
	updatePolicyInactivated,
	updatePolicyActivated,
	updatePolicyTest,
	updatePolicyUnsetTest,
	onPage,
	timeStr,
	refetch,
	handleGetTextContent,
	handleGetTextContentToView,
	onDownloadXML,
}: TProps) => {
	const navigate = useNavigate();

	const {roles} = useAppSelector(state => state.user);

	const [pageCount, setPageCount] = useState(0);
	const [openModalUpload, setOpenModalUpload] = useState(false);

	/* Setting the pageCount to the ceiling of the totalPolicies divided by the limit. */
	useEffect(
		() => setPageCount(Math.ceil(totalPolicies / LIMIT)),
		[totalPolicies],
	);

	/**
	 * It takes an object with a property called selected, which is a number, and then it sets the
	 * currentPage to that number plus one, and then it calls the handleGetData function with the
	 * currentPage as an argument, and then it sets the items to the result of that function, and then it
	 * sets the pageCount to the ceiling of the totalPolicies divided by the limit
	 * @param {TData}  - TData - this is the type of the object that is passed to the function.
	 */
	const handlePageClick = ({selected}: TData) => {
		const currentPage = selected;

		onPage(currentPage);
		setPageCount(Math.ceil(totalPolicies / LIMIT));
	};

	/**
	 * It returns an object with the properties that the modal component needs to render
	 * @param {Policy} item - Policy - The item that is being passed to the modal.
	 */
	const optionsRemoveModal = (item: Policy) => ({
		title: 'Do you really want to remove this policy?',
		message: 'This action can not be undone.',
		buttons: [
			{
				label: 'Yes',
				onClick: () => {
					deletePolicy(item);
				},
			},
			{
				label: 'No',
			},
		],
		closeOnEscape: true,
		closeOnClickOutside: true,
		keyCodeForClose: [8, 32],
		overlayClassName: 'overlay-custom-class-name',
	});

	/**
	 * A function that takes in a Policy object and returns a function that calls the confirmAlert
	 * function with the optionsRemoveModal function.
	 * @param {Policy} item - Policy - this is the item that is being passed to the function.
	 */
	const handleRemove = (item: Policy) => confirmAlert(optionsRemoveModal(item));

	const handleChangeRadio = async (item: Policy) => {
		try {
			if (item.state === 'INACTIVE') {
				await Promise.resolve(updatePolicyActivated(item));
			}
			refetch(); // Refresh the ActivePolicy panel after the update is complete
		} catch (error) {
			console.error('Error updating policy state:', error);
		}
	};

	const handleChangeRadioTest = async (item: Policy) => {
		try {
			if (item.state === 'INACTIVE') {
				await Promise.resolve(updatePolicyTest(item));
			} else {
				await Promise.resolve(updatePolicyUnsetTest(item));
			}
			refetch(); // Refresh the ActivePolicy panel after the update is complete
		} catch (error) {
			console.error('Error updating policy state:', error);
		}
	};

	/**
	 * This function toggles the state of a modal window.
	 */
	const handleModalUpload = () => setOpenModalUpload(prev => !prev);

	const isReadOnly = hasExactRole(roles, 'policy_engine_read_only');

	return (
		<div className="card-tasks card h-100">
			<div className="card-header">
				<i className="bi bi-clock-history mr-3 float-left text-warning bi-2" />
				<h2 className="card-title">Repository of Policy Sets</h2>
				<p className="card-category">
					Sets of policies stored in the PCRF. Only one can be active.
				</p>
			</div>
			<div className="card-body pt-0 mt-n4">
				<div
					className={`d-flex flex-row-reverse mb-1 ${
						isReadOnly ? 'pt-4' : ''
					}`}>
					{!isReadOnly && (
						<>
							<button
								className="border-0 bg-transparent d-inline"
								onClick={() => {
									navigate('/policy/view', {
										state: {},
									}) as never;
								}}>
								<i
									className="bi-1 bi-plus-square"
									title=""
									data-toggle="tooltip"
									data-original-title="Create"
								/>
							</button>
							<button
								className="border-0 bg-transparent d-inline"
								data-toggle="modal"
								data-target="#ModalUploadFile"
								onClick={handleModalUpload}>
								<i
									className="bi-1 bi-upload"
									title=""
									data-toggle="tooltip"
									data-original-title="Upload"
								/>
							</button>
						</>
					)}
				</div>
				<div className="card-body table-responsive p-0 cypress-policySetContainer">
					<table className="table table-striped table-sm table-head-fixed text-nowrap mode-test">
						<thead>
							<tr>
								<th className="col_1">ID</th>
								<th className="col pl-5">Description</th>
								<th className="col_3">Actions</th>
							</tr>
						</thead>
						<tbody>
							{getAllPolicies.length ? (
								getAllPolicies.map((item: Policy, key: number) => {
									const name = `${item.title} v${item.version}`;
									return (
										<tr
											key={key}
											className={
												item.state === 'TESTING' ? 'TestActive' : 'TestInactive'
											}>
											<td>{name}</td>
											<td className="pl-5">{item.description}</td>
											<td>
												<div
													className="custom-control custom-switch d-inline"
													title={isReadOnly ? 'Activate' : ''}
													data-toggle="tooltip"
													data-original-title="On/Off">
													<input
														disabled={isReadOnly || item.state !== 'INACTIVE'}
														type="checkbox"
														defaultValue={item.state}
														className="custom-control-input"
														id={`${item.title}-${item.version}`}
														checked={item.state === 'ACTIVE' ? true : false}
														onChange={() => {
															void handleChangeRadio(item);
														}}
													/>
													<label
														className="custom-control-label"
														htmlFor={`${item.title}-${item.version}`}
													/>
												</div>
												{/* condition buttons */}
												{item.state === 'INACTIVE' ? (
													<button
														className="border-0 bg-transparent d-inline"
														onClick={() =>
															handleGetTextContent(item, true, true)
														}>
														<i
															className={`bi-1 ${
																isReadOnly ? 'bi-eye' : 'bi-pencil'
															}`}
															title=""
															data-toggle="tooltip"
															data-original-title="Search"
														/>
													</button>
												) : (
													<button
														className="border-0 bg-transparent d-inline"
														onClick={() =>
															handleGetTextContent(item, true, false)
														}>
														<i
															className={`bi-1 ${
																isReadOnly ? 'bi-eye' : 'bi-pencil'
															}`}
															title=""
															data-toggle="tooltip"
															data-original-title="Search"
														/>
													</button>
												)}

												<button
													className="ml-1 border-0 bg-transparent d-inline"
													onClick={() => onDownloadXML(item)}>
													<i
														className="bi-1 bi-download"
														title=""
														data-toggle="tooltip"
														data-original-title="Download"
													/>
												</button>

												<button
													onClick={() => {
														handleGetTextContentToView(item);
													}}
													className="ml-1 border-0 bg-transparent d-inline"
													data-toggle="modal"
													data-target="#modalSelectedPolicyView">
													<i
														className="bi-1 bi-filetype-xml"
														title=""
														data-toggle="tooltip"
														data-original-title="XML"
													/>
												</button>

												{!isReadOnly ? (
													<div
														className="ml-2 custom-control custom-switch d-inline mode-test"
														title={
															item.state === 'TESTING'
																? 'Stop test'
																: 'Test it!'
														}
														data-toggle="tooltip"
														data-original-title="On/Off">
														<input
															disabled={isReadOnly || item.state === 'ACTIVE'}
															type="checkbox"
															defaultValue={item.state}
															className="custom-control-input"
															id={`${item.title}-${item.version}-test`}
															checked={item.state === 'TESTING' ? true : false}
															onChange={() => {
																void handleChangeRadioTest(item);
															}}
														/>
														<label
															className="custom-control-label"
															htmlFor={`${item.title}-${item.version}-test`}
														/>
													</div>
												) : null}
												{item.state === 'INACTIVE' && !isReadOnly ? (
													<button
														className="border-0 bg-transparent d-inline ml-1"
														onClick={() => handleRemove(item)}>
														<i
															className="bi-1 bi-trash-fill text-danger"
															title=""
															data-toggle="tooltip"
															data-original-title="Delete"
														/>
													</button>
												) : null}
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={2} className="text-center">
										No Policy Set created
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			<ReactPaginate
				previousLabel="Previous"
				nextLabel="Next"
				breakLabel="..."
				pageCount={pageCount}
				marginPagesDisplayed={2}
				pageRangeDisplayed={2}
				onPageChange={handlePageClick as never}
				containerClassName="pagination justify-content-center"
				pageClassName="page-item"
				pageLinkClassName="page-link"
				previousClassName="page-item"
				previousLinkClassName="page-link"
				nextClassName="page-item"
				nextLinkClassName="page-link"
				breakClassName="page-item"
				breakLinkClassName="page-link"
				activeClassName="active"
			/>

			<div className="card-footer">
				<hr />
				<div className="stats">
					<i className="bi-1 bi-clock-history" /> Updated {timeStr}
				</div>
			</div>

			<ModalUploadFile
				type="Policy"
				open={openModalUpload}
				onClose={handleModalUpload}
				refetch={refetch}
			/>
		</div>
	);
};

export default RepositoryPolicy;
